import { ERROR_CODES } from "./portalMessages.js"

export const TIME_INTERVAL_MS = 1000

const PASS_THROUGH = {
    play: "play",
    resume: "resume",
    pause: "pause",
    ended: "ended",
    mute: "mute",
    unmute: "unmute",
    fullscreen_enter: "fullscreen_enter",
    fullscreen_exit: "fullscreen_exit",
    quality_switch: "quality_switch",
    buffering_start: "buffering_start",
    buffering_end: "buffering_end",
}

const finiteOrNull = (value) => (Number.isFinite(value) ? value : null)
const seconds = (value) => Math.max(0, finiteOrNull(value) ?? 0)

/**
 * SC-1323 §4.3: maps PlayerShell's playerEvent stream onto portal messages (§5.2). It never calls
 * a podcast.* route: the portal turns these into beats for Pro (website lessonProgress.js). The
 * switcher's own pause is left out (the `mode` event closes the item left), as are beats (the
 * portal keeps its own 15 s grid from `time`).
 */
export function createPortalAnalytics({ post, now = () => Date.now() }) {
    let lastTimeAt = -Infinity
    const reported = new Set()

    const base = (event) => ({ item_id: event.itemId, position: seconds(event.position), duration: finiteOrNull(event.duration), mode: event.mode })

    function videoFields(event) {
        if (event.mode !== "video") {
            return {}
        }

        const fields = {}

        if (event.viewport && Number.isInteger(event.viewport.width) && Number.isInteger(event.viewport.height)) {
            fields.viewport_pixels = { width: Math.max(0, event.viewport.width), height: Math.max(0, event.viewport.height) }
        }

        if (typeof event.fullscreen === "boolean") {
            fields.fullscreen = event.fullscreen
        }

        // Same condition as the podcast beacons (useVideoAnalytics.js): on every video event,
        // whenever the current rendition's height is known.
        if (typeof event.qualityHeight === "number" && Number.isFinite(event.qualityHeight)) {
            fields.quality_height = Math.max(0, Math.round(event.qualityHeight))
        }

        return fields
    }

    return {
        handle(event) {
            switch (event.type) {
                case "time":
                    if (now() - lastTimeAt < TIME_INTERVAL_MS) {
                        return
                    }

                    lastTimeAt = now()
                    post("time", { ...base(event), playing: Boolean(event.playing) })

                    return
                case "seek":
                    post("event", { ...base(event), action: "seek", from: seconds(event.from), ...videoFields(event) })

                    return
                case "mode_switch":
                    post("event", {
                        item_id: event.fromItemId,
                        action: "mode",
                        position: seconds(event.position),
                        duration: finiteOrNull(event.duration),
                        mode: event.fromMode,
                        from_item: event.fromItemId,
                        to_item: event.toItemId,
                        to_mode: event.toMode,
                        // Leaving a video: the closing event carries the video fields of the item left.
                        ...videoFields({ ...event, mode: event.fromMode }),
                    })

                    return
                case "cta_click":
                    if (typeof event.ctaId === "string" && event.ctaId !== "") {
                        post("event", { ...base(event), action: "cta", cta_id: event.ctaId, ...videoFields(event) })
                    }

                    return
                case "pause":
                    if (event.switching) {
                        return
                    }
                    break
            }

            const action = PASS_THROUGH[event.type]

            if (!action) {
                return
            }

            const extra = {}

            if (event.type === "buffering_end" && Number.isFinite(event.bufferingMs)) {
                extra.buffering_ms = Math.max(0, Math.round(event.bufferingMs))
            }

            post("event", { ...base(event), action, ...extra, ...videoFields(event) })
        },
        error(code) {
            if (ERROR_CODES.includes(code) && !reported.has(code)) {
                reported.add(code)
                post("error", { code })
            }
        },
    }
}
